import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { LampLight } from "@/components/LampLight";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthModal = () => {
  const { authModal, setAuthModal, applyAuth } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpMode, setOtpMode] = useState(false); // sign in with an emailed code
  const [cooldown, setCooldown] = useState(0);
  const [lampOn, setLampOn] = useState(true);
  const [lampMood, setLampMood] = useState("idle");
  const [pulse, setPulse] = useState(0);

  const isSignUp = authModal === "signup";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    setOtpStep(false);
    setOtp("");
    setErr("");
    setOtpMode(false);
    setLampMood("idle");
    setLampOn(true);
    setCooldown(0);
  }, [authModal]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const flashMood = (m) => {
    setLampMood(m);
    setTimeout(() => setLampMood("idle"), 1400);
  };

  const close = () => {
    setAuthModal(null);
    setErr("");
  };

  const resetAll = () => {
    setForm({ name: "", email: "", phone: "", password: "" });
    setOtpStep(false);
    setOtp("");
    setOtpMode(false);
    setErr("");
  };

  const sendCode = async () => {
    setBusy(true);
    setErr("");
    try {
      const purpose = isSignUp ? "register" : "login";
      const r = await fetch(`${API}/auth/otp/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), purpose }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d && d.detail ? d.detail : "Could not send the code — please try again.");
      setOtpStep(true);
      setOtp("");
      setCooldown(60);
      setPulse((p) => p + 1);
      toast.success(`Code sent to ${form.email.trim()} — check your inbox (and spam).`);
    } catch (e2) {
      setErr(e2 && e2.message ? e2.message : "Could not send the code.");
      flashMood("error");
    } finally {
      setBusy(false);
    }
  };

  const finishAuth = (d, successMsg) => {
    applyAuth(d);
    setLampMood("success");
    toast.success(successMsg);
    resetAll();
    setTimeout(() => close(), 700);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      if (otpStep) {
        const r = isSignUp
          ? await fetch(`${API}/auth/register`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ name: form.name, email: form.email.trim(), phone: form.phone, password: form.password, otp }),
            })
          : await fetch(`${API}/auth/login/otp`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email: form.email.trim(), otp }),
            });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d && d.detail ? d.detail : "Something went wrong — please try again.");
        finishAuth(
          d,
          isSignUp
            ? `Bonjour ${d.user.name}! Welcome to French Pie — your first order gets 10% off, applied automatically.`
            : `Bon retour, ${d.user.name}!`,
        );
        return;
      }
      if (isSignUp || otpMode) {
        await sendCode();
        return;
      }
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), password: form.password }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d && d.detail ? d.detail : "Something went wrong — please try again.");
      finishAuth(d, `Bon retour, ${d.user.name}!`);
    } catch (e2) {
      setErr(e2 && e2.message ? e2.message : "Something went wrong — please try again.");
      flashMood("error");
    } finally {
      setBusy(false);
    }
  };

  if (!authModal) return null;
  const showPassword = !otpStep && !otpMode;
  const submitLabel = otpStep ? "Verify code" : isSignUp ? "Send code & continue" : otpMode ? "Email me a code" : "Sign in";

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center px-4" data-testid="auth-modal">
      <div className="absolute inset-0 bg-[#1C130E]/60 backdrop-blur-sm" onClick={close} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-3xl bg-[#FDFBF7] border border-[rgba(28,19,14,0.1)] shadow-2xl px-7 py-6">
        <button
          onClick={close}
          data-testid="auth-close-btn"
          aria-label="Close sign in"
          className="absolute top-4 right-4 z-10 grid place-items-center w-9 h-9 rounded-full hover:bg-[#1C130E]/[0.06] text-[#1C130E]/60 transition-colors"
        >
          <X size={16} />
        </button>
        <LampLight on={lampOn} mood={lampMood} pulse={pulse} onToggle={() => setLampOn((v) => !v)} />
        <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#C85A32] mb-3">
          French Pie account
        </p>
        <div className="flex gap-6 border-b border-[rgba(28,19,14,0.1)] mb-6">
          {["signin", "signup"].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setAuthModal(tab);
                setErr("");
                setOtpStep(false);
                setOtp("");
                setOtpMode(false);
              }}
              data-testid={`auth-tab-${tab}`}
              className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                authModal === tab ? "border-[#C85A32] text-[#1C130E]" : "border-transparent text-[#1C130E]/45 hover:text-[#C85A32]"
              }`}
            >
              {tab === "signin" ? "Sign in" : "Sign up"}
            </button>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-4">
          {otpStep && (
            <p className="text-sm text-[#1C130E]/70 leading-relaxed" data-testid="auth-otp-hint">
              We emailed a 6-digit code to <strong>{form.email.trim()}</strong>. It expires in 10 minutes.
            </p>
          )}
          {!otpStep && isSignUp && (
            <>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Your name</span>
                <input
                  className="field mt-1.5"
                  data-testid="auth-name-input"
                  placeholder="Ayesha"
                  value={form.name}
                  onChange={set("name")}
                  minLength={2}
                  maxLength={80}
                  required
                />
              </label>
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Phone</span>
                <input
                  className="field mt-1.5"
                  data-testid="auth-phone-input"
                  placeholder="10-digit mobile"
                  value={form.phone}
                  onChange={set("phone")}
                  pattern="[0-9+\-]{10,15}"
                  required
                />
              </label>
            </>
          )}
          <label className="block">
            <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Email</span>
            <input
              className="field mt-1.5 disabled:opacity-60"
              type="email"
              disabled={otpStep}
              data-testid="auth-email-input"
              placeholder="you@example.com"
              value={form.email}
              onChange={set("email")}
              required
            />
          </label>
          {showPassword && (
            <label className="block">
              <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">Password</span>
              <input
                className="field mt-1.5"
                type="password"
                data-testid="auth-password-input"
                placeholder={isSignUp ? "At least 8 characters" : "Your password"}
                value={form.password}
                onChange={set("password")}
                minLength={isSignUp ? 8 : undefined}
                required
              />
            </label>
          )}
          {otpStep && (
            <label className="block">
              <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#1C130E]/55">6-digit code</span>
              <input
                className="field mt-1.5 text-center font-mono-accent text-xl tracking-[0.5em]"
                inputMode="numeric"
                autoComplete="one-time-code"
                data-testid="auth-otp-input"
                placeholder="••••••"
                maxLength={6}
                pattern="[0-9]{6}"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
              />
            </label>
          )}
          {err && (
            <p className="text-sm text-red-600" data-testid="auth-error">
              {err}
            </p>
          )}
          <button
            type="submit"
            disabled={busy || (otpStep && otp.length !== 6)}
            data-testid="auth-submit-btn"
            className="w-full rounded-full bg-[#1C130E] text-[#FDFBF7] py-3.5 text-sm font-semibold hover:bg-[#C85A32] transition-colors disabled:opacity-60"
          >
            {busy ? "One moment…" : submitLabel}
          </button>
          {otpStep ? (
            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => {
                  setOtpStep(false);
                  setOtp("");
                  setErr("");
                }}
                data-testid="auth-back-btn"
                className="text-[#1C130E]/50 hover:text-[#C85A32] transition-colors"
              >
                ← Change details
              </button>
              <button
                type="button"
                onClick={sendCode}
                disabled={busy || cooldown > 0}
                data-testid="auth-resend-btn"
                className="font-semibold text-[#C85A32] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          ) : isSignUp ? (
            <p className="text-xs text-[#1C130E]/50 text-center leading-relaxed">
              We'll email you a one-time code to verify it's you. Your details pre-fill at checkout and your orders land in one place.
            </p>
          ) : (
            <p className="text-xs text-[#1C130E]/50 text-center leading-relaxed">
              {otpMode ? (
                <>
                  Prefer your password?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setOtpMode(false);
                      setErr("");
                    }}
                    data-testid="auth-password-mode-btn"
                    className="font-semibold text-[#C85A32] hover:underline"
                  >
                    Sign in with password
                  </button>
                </>
              ) : (
                <>
                  New here? Switch to Sign up — it takes ten seconds. Or{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setOtpMode(true);
                      setErr("");
                    }}
                    data-testid="auth-otp-mode-btn"
                    className="font-semibold text-[#C85A32] hover:underline"
                  >
                    sign in with an email code
                  </button>
                  .
                </>
              )}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};